import { useUserAccountRole } from "./useUserAccountRole";
import { useCurrentConnection } from "../../components/ConnectionContext";
import { Authentication } from "../authentication/useQueryAuthentications";
interface Props {
  connection?: Authentication;
}

export function useConnectionActionPermissions({
  connection: connectionArgument,
}: Props) {
  const { customuserId, isAccountOwner, isSuperAdmin, isAdmin, isMember } =
    useUserAccountRole();

  const connectionContext = useCurrentConnection();

  const targetConnection = connectionArgument || connectionContext;

  const isAdminPlus = !!(isAccountOwner || isSuperAdmin || isAdmin);

  const isManagedConnection = !!targetConnection?.isManaged;
  const isConnectionOwner = customuserId === targetConnection?.owner?.id;

  // Only account owners, super admins, and admins can filter by private connections
  const canFilterByPrivateAndSharedWithEveryone = isAdminPlus;

  const isMemberWithAccess =
    isMember &&
    (targetConnection?.members?.some((member) => member.id === customuserId) ||
      targetConnection?.groups?.some((group) => group.id === "all"));

  const allAccess = !!(
    isAccountOwner ||
    isSuperAdmin ||
    isAdmin ||
    isConnectionOwner ||
    isMemberWithAccess
  );

  const canFilterByOwnerStatus = !!(isAccountOwner || isSuperAdmin);

  // Only admins+ can create managed (shared-credential) connections; members
  // get the plain personal "Add connection" flow with no managed option.
  const canCreateManagedConnection = !!(
    isAccountOwner ||
    isSuperAdmin ||
    isAdmin
  );

  const canConvertConnection = !!(isAccountOwner || isSuperAdmin);

  return {
    canCreateConnection: allAccess,
    canCreateManagedConnection,
    canConvertConnection,
    canDeleteConnection: !!targetConnection?.permissions?.delete,
    canFilterByOwnerStatus,
    canFilterByPrivateAndSharedWithEveryone,
    canReconnectConnection:
      !!targetConnection?.permissions?.reconnect &&
      (isConnectionOwner || (isAdminPlus && isManagedConnection)),
    canReplaceConnection:
      !!targetConnection?.permissions?.edit &&
      (isConnectionOwner || (isAdminPlus && isManagedConnection)),
    canRenameConnection: !!targetConnection?.permissions?.edit,
    canShareConnection: !!targetConnection?.permissions?.share,
    canTestConnection: !!targetConnection?.permissions?.test,
    canTransferConnection: !!targetConnection?.permissions?.transfer,
  };
}
